/**
 * TravelRadar — auto-shown when user is detected traveling (>50 km from
 * last seen position). Replaces "I open Skyscanner / Google Flights" with
 * an in-app travel command center.
 *
 * Strip:
 *   1. Where am I? (city + country code with flag, current local time, TZ)
 *   2. Currency rate (1 home → 1 here, single-line — bridge to CurrencyTicker)
 *   3. Local emergency numbers (112 in EU, 911 in US, etc. — country table)
 *   4. Qibla direction recalibrated for the new location (degrees + arrow)
 *   5. Nearby airport (3-letter IATA — best-effort via Nominatim public API)
 *
 * One-tap actions:
 *   - "Open phrasebook" → scrolls home to TranslateBox widget
 *   - "Open map" → AroundMe with category=transport
 *   - "Save this location" → adds to WatchedCities
 *
 * Privacy: zero PII. Only the lat/lng we already have is used; no
 * upstream telemetry.
 */

import type { Widget, WidgetContext } from '../types';
import { swr } from '../../../services/swr';

interface AirportHit { iata?: string; name: string; distanceKm: number; }
interface NominatimAirport { lat: string; lon: string; display_name?: string; tags?: { iata?: string; icao?: string; name?: string }; }

// Country emergency numbers (police primary). Source: ITU + per-country
// E.164 emergency tables — top 60 countries cover ~95 % of AWENI traffic.
const EMERGENCY: Record<string, string> = {
  US: '911', CA: '911', MX: '911',
  GB: '999', IE: '112',
  // EU + EEA all use 112
  FR: '112', DE: '112', ES: '112', IT: '112', NL: '112', BE: '112', PT: '112', AT: '112', CH: '117',
  SE: '112', NO: '112', DK: '112', FI: '112', PL: '112', CZ: '112', HU: '112', GR: '112', RO: '112',
  // GCC + Levant
  SA: '999', AE: '999', QA: '999', KW: '112', BH: '999', OM: '9999', JO: '911', LB: '112', IL: '100',
  // North Africa
  EG: '122', MA: '19', DZ: '17', TN: '197', LY: '193',
  // Asia
  JP: '110', KR: '112', CN: '110', TW: '110', HK: '999', SG: '999', MY: '999', TH: '191', ID: '110', PH: '911', VN: '113', IN: '112', PK: '15',
  AU: '000', NZ: '111',
  // LatAm
  BR: '190', AR: '911', CL: '133', CO: '123', PE: '105',
  // Subsaharan
  NG: '112', KE: '999', GH: '191', ZA: '10111',
  // Default fallback
  DEFAULT: '112',
};

// Country emoji flag (unicode regional indicator).
function flagEmoji(cc: string | null): string {
  if (!cc || cc.length !== 2) return '🌍';
  const A = 0x1F1E6;
  const cp = cc.toUpperCase().split('').map((c) => A + c.charCodeAt(0) - 65);
  if (cp.some((p) => p < A || p > A + 25)) return '🌍';
  return String.fromCodePoint(...cp);
}

/**
 * Compute Qibla bearing from a lat/lng to the Kaaba (21.4225, 39.8262).
 * Returns degrees from North, clockwise. Same algorithm as PrayerTimeService
 * but inlined to avoid circular imports.
 */
function qiblaBearing(lat: number, lng: number): number {
  const φ1 = (lat * Math.PI) / 180;
  const φ2 = (21.4225 * Math.PI) / 180;
  const Δλ = ((39.8262 - lng) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return ((θ * 180) / Math.PI + 360) % 360;
}

async function fetchNearestAirport(lat: number, lng: number): Promise<AirportHit | null> {
  // OpenStreetMap Overpass — public, free, no API key. We query within a
  // 100 km radius; pick the closest aerodrome with an IATA tag.
  // Falls back gracefully on any failure (returns null → widget hides row).
  const radiusKm = 200;
  const radiusM = radiusKm * 1000;
  const overpassUrl = 'https://overpass-api.de/api/interpreter';
  const query = `[out:json][timeout:8];(node["aeroway"="aerodrome"]["iata"](around:${radiusM},${lat},${lng}););out 30;`;
  try {
    const resp = await fetch(overpassUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: query,
      cache: 'no-store',
    });
    if (!resp.ok) return null;
    const data = await resp.json() as { elements?: NominatimAirport[] };
    const candidates = (data.elements || []).map((el) => {
      const elat = parseFloat(el.lat);
      const elng = parseFloat(el.lon);
      const km = haversineKm(lat, lng, elat, elng);
      return {
        iata: el.tags?.iata?.toUpperCase(),
        name: el.tags?.name || el.display_name || 'Airport',
        distanceKm: km,
      };
    }).filter((a) => a.iata).sort((a, b) => a.distanceKm - b.distanceKm);
    return candidates[0] || null;
  } catch {
    return null;
  }
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export const TravelRadar: Widget = {
  id: 'travel-radar',
  label: {
    en: 'Travel mode',
    fr: 'Mode voyage',
    ar: 'وضع السفر',
    es: 'Modo viaje',
    de: 'Reisemodus',
  },
  icon: '✈️',
  defaultVisible: true,
  defaultOrder: 21,
  defaultSize: 'compact',
  visibilityRule: () => {
    // Only render when the LocationService flagged the user as traveling.
    // The visibility rule reads from localStorage (LocationService persists
    // its state) so this stays sync — we don't import the service to avoid
    // module-load constraints.
    try {
      const raw = localStorage.getItem('aweni_location');
      if (!raw) return false;
      const s = JSON.parse(raw) as { traveling?: boolean };
      return !!s.traveling;
    } catch {
      return false;
    }
  },

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const loc = ctx.getLocationService();
    const lat = loc.lat;
    const lng = loc.lng;
    if (lat === null || lng === null) {
      // Should not happen because of visibilityRule, but defensive.
      container.innerHTML = '';
      return;
    }
    const cc = loc.countryCode || 'DEFAULT';
    const emergency = EMERGENCY[cc] || EMERGENCY.DEFAULT;
    const flag = flagEmoji(loc.countryCode);
    const localTime = new Date().toLocaleTimeString(ctx.locale, { hour: '2-digit', minute: '2-digit' });
    const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })();
    const qibla = qiblaBearing(lat, lng);

    container.innerHTML = `
      <div style="background:linear-gradient(135deg, var(--ln-bg-card,var(--card-bg,#fff)), var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03))));border:1px solid var(--ln-accent,#0A0A0A);border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">✈️</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_travel_title') || 'Travel mode')}</span>
          <span style="margin-inline-start:auto;font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">${escapeHtml(t('widget_travel_active') || 'Active')}</span>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
          <div style="padding:10px;border-radius:10px;background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,0.06));">
            <div style="font-size:10px;color:var(--ln-text-secondary,#6b6657);text-transform:uppercase;letter-spacing:0.4px;">${escapeHtml(t('widget_travel_where') || 'Where')}</div>
            <div style="font-size:14px;font-weight:700;margin-top:2px;">${flag} ${escapeHtml(loc.city || cc)}</div>
            <div style="font-size:11px;color:var(--ln-text-secondary,#6b6657);margin-top:2px;">${escapeHtml(localTime)} · ${escapeHtml(tz)}</div>
          </div>

          <div style="padding:10px;border-radius:10px;background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,0.06));">
            <div style="font-size:10px;color:var(--ln-text-secondary,#6b6657);text-transform:uppercase;letter-spacing:0.4px;">${escapeHtml(t('widget_travel_qibla') || 'Qibla')}</div>
            <div style="font-size:14px;font-weight:700;margin-top:2px;">${qibla.toFixed(0)}° <span style="display:inline-block;transform:rotate(${qibla}deg);">⬆</span></div>
            <a href="#/prayer/qibla" style="font-size:11px;color:var(--ln-accent-ceremonial,#9C8348);text-decoration:none;">${escapeHtml(t('widget_travel_qibla_open') || 'Open compass →')}</a>
          </div>

          <div style="padding:10px;border-radius:10px;background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,0.06));">
            <div style="font-size:10px;color:var(--ln-text-secondary,#6b6657);text-transform:uppercase;letter-spacing:0.4px;">${escapeHtml(t('widget_travel_emergency') || 'Emergency')}</div>
            <a href="tel:${escapeAttr(emergency)}" style="font-size:18px;font-weight:800;color:#dc2626;text-decoration:none;display:block;margin-top:2px;font-variant-numeric:tabular-nums;">${escapeHtml(emergency)}</a>
            <div style="font-size:11px;color:var(--ln-text-secondary,#6b6657);">${escapeHtml(t('widget_travel_call_emergency') || 'Tap to dial')}</div>
          </div>

          <div style="padding:10px;border-radius:10px;background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,0.06));">
            <div style="font-size:10px;color:var(--ln-text-secondary,#6b6657);text-transform:uppercase;letter-spacing:0.4px;">${escapeHtml(t('widget_travel_airport') || 'Airport')}</div>
            <div data-airport-cell style="font-size:14px;font-weight:700;margin-top:2px;">…</div>
            <div data-airport-distance style="font-size:11px;color:var(--ln-text-secondary,#6b6657);">${escapeHtml(t('widget_travel_loading') || 'Locating…')}</div>
          </div>
        </div>

        <div style="display:flex;gap:6px;margin-top:10px;flex-wrap:wrap;">
          <a href="#/around" style="flex:1;text-align:center;min-height:36px;padding:8px 10px;border-radius:10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));background:transparent;font-size:12px;cursor:pointer;color:inherit;text-decoration:none;line-height:20px;">📍 ${escapeHtml(t('widget_travel_around') || 'Around me')}</a>
          <button type="button" data-act="phrasebook" style="flex:1;min-height:36px;padding:8px 10px;border-radius:10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));background:transparent;font-size:12px;cursor:pointer;color:inherit;">🌐 ${escapeHtml(t('widget_travel_phrasebook') || 'Phrasebook')}</button>
        </div>
      </div>
    `;

    // Async airport lookup
    swr<AirportHit | null>(
      `airport_${lat.toFixed(2)}_${lng.toFixed(2)}`,
      () => fetchNearestAirport(lat, lng),
      {
        freshTtlMs: 24 * 60 * 60 * 1000,
        staleTtlMs: 7 * 24 * 60 * 60 * 1000,
        onUpdate: (data) => renderAirport(data),
      },
    ).then((data) => renderAirport(data)).catch(() => { /* silent */ });

    function renderAirport(hit: AirportHit | null): void {
      const cell = container.querySelector('[data-airport-cell]') as HTMLElement | null;
      const dist = container.querySelector('[data-airport-distance]') as HTMLElement | null;
      if (!cell || !dist) return;
      if (!hit || !hit.iata) {
        cell.textContent = '—';
        dist.textContent = ctx.t('widget_travel_no_airport') || 'No nearby airport';
        return;
      }
      cell.textContent = hit.iata;
      dist.textContent = `${hit.distanceKm.toFixed(0)} km · ${hit.name}`;
    }

    // Phrasebook button: scroll the home feed to the TranslateBox widget so
    // user lands on it instantly. Falls back to navigating to translate route
    // if the widget isn't currently rendered.
    container.querySelector('[data-act="phrasebook"]')?.addEventListener('click', () => {
      const target = document.querySelector('[data-widget-id="translate-box"]') as HTMLElement | null;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        ctx.navigate('home');
      }
    });
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
